import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hold the screen awake while something is actively running on it.
 *
 * WHY THIS EXISTS. A phone blanks its display after 30 to 60 seconds, and on Android Chrome a
 * locked screen suspends audio capture. Without this, "record on your phone" works for as long
 * as someone keeps tapping the screen and stops silently during any real encounter. That is the
 * whole reason it is here: it is not a polish item, it is the difference between the feature
 * working and appearing to work.
 *
 * ⚠️ WHAT IT COSTS, AND THE THING IT OBLIGES. Holding this lock deliberately defeats the
 * platform's own auto-lock, which until now was doing our privacy work for free: a phone put
 * down on a counter used to blank itself. It no longer will. So a screen holding this lock has
 * to answer for whatever is on it, either by covering it (useIdleBlur) or by not putting
 * anything sensitive there in the first place, which is the route the phone recording page
 * took. Do not add a transcript or a chart to a screen that holds this lock without also
 * adding the cover.
 *
 * ⚠️ THE NON-OBVIOUS BIT. The sentinel is released by the browser whenever the page is hidden,
 * and it does NOT come back by itself when the page is shown again. A tab switch or a glance at
 * a notification would otherwise quietly end the lock for the rest of the encounter, so
 * visibilitychange re-requests it.
 *
 * Every call is feature-detected and wrapped: the API is absent on older Safari, and a request
 * can be refused outright (low battery, no preceding user gesture). A refusal is not an error
 * worth surfacing as one, so it reports through `held` and the caller decides whether to ask
 * the person to keep the screen on themselves.
 *
 * @param {boolean} active - Whether the screen should be held awake right now.
 * @returns {{ held: boolean, supported: boolean }}
 */
const wakeLockSupported = () =>
  typeof navigator !== "undefined" && "wakeLock" in navigator;

function useScreenWakeLock(active) {
  const [held, setHeld] = useState(false);
  const sentinelRef = useRef(null);
  // Read inside async callbacks that may resolve after `active` has already flipped back.
  const activeRef = useRef(active);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const release = useCallback(async () => {
    const sentinel = sentinelRef.current;
    sentinelRef.current = null;
    setHeld(false);
    if (!sentinel) return;
    try {
      await sentinel.release();
    } catch {
      // Already released by the browser. Nothing to undo.
    }
  }, []);

  const request = useCallback(async () => {
    if (!wakeLockSupported() || sentinelRef.current) return;
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      // Recording may have stopped while the request was in flight. Handing the lock back
      // immediately is the only way to avoid holding a phone awake after the encounter ended.
      if (!activeRef.current) {
        try {
          await sentinel.release();
        } catch {
          /* nothing to undo */
        }
        return;
      }
      sentinelRef.current = sentinel;
      setHeld(true);
      sentinel.addEventListener?.("release", () => {
        if (sentinelRef.current === sentinel) sentinelRef.current = null;
        setHeld(false);
      });
    } catch {
      // Refused. The caller shows its own "please keep this screen on" copy.
      setHeld(false);
    }
  }, []);

  useEffect(() => {
    if (active) {
      request();
    } else {
      release();
    }
    // Unmount is the case that matters here: navigating away mid-recording must not leave a
    // phone pinned awake.
    return () => {
      release();
    };
  }, [active, request, release]);

  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && activeRef.current) request();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, [active, request]);

  return { held, supported: wakeLockSupported() };
}

export default useScreenWakeLock;
